import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import {
    Button,
    Modal,
    FieldTextInput,
    FieldSelect,
    FieldCurrencyInput,
    Form,
    Heading,
} from "../../components";

import appSettings from '../../config/settings';
import { FormattedMessage } from '../../util/reactIntl';
import { supportedParnexusCurrencies } from '../../util/currency';
import {
    principalBuyerOptions,
    buyerProfileOptions,
    fundsOptions,
    sourceOfFundsOptions,
    proofOfFundsOptions,
    completionOptions,
    dealStructureOptions,
    countryOptions,
} from '../../util/applicationFormsOptions';

import { getTransactionTypeData } from '../CheckoutPage/CheckoutPageTransactionHelpers';
import { getProcess } from '../../transactions/transaction';
import {
    required,
    composeValidators,
    validateNoSensitiveInfoInMessage,
    validateInputField,
} from '../../util/validators';
import { pathByRouteName } from '../../util/routes';

import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import css from './BuyerFormModal.module.css';

const BuyerFormModal = (props) => {
    const {
        onClose,
        isOpen,
        onManageDisableScrolling,
        processName,
        listing,
        currentUser,
        config,
        initiateInquiryInProgress,
        initiateInquiryError,
        onInitiateinquiryWithApplicationForm,
    } = props;

    const history = useHistory();
    const routeConfiguration = useRouteConfiguration();

    const marketplaceCurrency = config.currency;
    const currencyConfig = appSettings.getCurrencyFormatting(marketplaceCurrency);

    // No transaction exists yet on this form, so the blocked-message warning
    // email is given the listing id to link to instead.
    const listingId = listing?.id?.uuid;

    const validateMessage = value =>
        validateNoSensitiveInfoInMessage(value, currentUser, undefined, listingId);

    const onSubmit = (formValues) => {
        const inquiryData = {
            buyerProfile: formValues.buyerProfile,
            principalBuyerProfile: formValues.principalBuyerProfile,
            currency: formValues.currency,
            approximateBudget: formValues.approximateBudget?.amount / 100,
            notes: formValues.notes,
            fundsAvailability: formValues.fundsAvailability,
            sourceOfFunds: formValues.sourceOfFunds,
            proofOfFunds: formValues.proofOfFunds,
            completionTimeline: formValues.completionTimeline,
            dealStructure: formValues.dealStructure,
            countryLocation: formValues.countryLocation,
            type: "buyer-form",
        };

        const { listingType, transactionProcessAlias, unitType } = listing?.attributes?.publicData || {};

        const process = processName ? getProcess(processName) : null;
        const transitions = process.transitions;
        const transition = transitions.INQUIRE_SELL_LISTINGS;
        const messageDate = new Date().getTime();

        const inquiryParams = {
            listingId: listing?.id,
            protectedData: {
                inquiryMessage: '',
                listingType: listingType,
                ...getTransactionTypeData(listingType, unitType, config),
            },
            metadata: {
                lastMessageSender: 'customer',
                lastMessageDate: messageDate,
                inquiryMessageDate: messageDate,
                providerHasUnreadMessages: true,
                customerAllowedToChat: false,
                inquiryData,

                lastActionTime: messageDate,
                lastActionBy: "customer",
                lastActionType: "inquiry",
            },
        };

        onInitiateinquiryWithApplicationForm(inquiryParams, transactionProcessAlias, transition)
            .then(transactionId => {
                const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
                    id: transactionId.uuid,
                });
                history.push(orderDetailsPath);
                onClose();
            })
            .catch(err => {
                console.error(err);
            });
    };

    return (
        <Modal
            id='BuyerFormModal'
            onClose={onClose}
            closeButtonMessage={' '}
            containerClassName={css.buyerFormModal}
            usePortal
            isOpen={isOpen}
            onManageDisableScrolling={onManageDisableScrolling}
        >
            <div className={css.modalContent}>
                <Heading as="h4" className={css.Heading}>
                    <FormattedMessage id="BuyerFormModal.Heading" /></Heading>
                <p className={css.info}>
                    <FormattedMessage id="BuyerFormModal.info" />
                    <FormattedMessage id="BuyerFormModal.info2" />
                </p>
                <FinalForm
                    onSubmit={onSubmit}
                    render={({ handleSubmit, pristine, invalid }) => (
                        <Form onSubmit={handleSubmit}>
                            <div className={css.formField}>
                                <FieldSelect
                                    name="buyerProfile"
                                    id="buyerProfile"
                                    label="Buyer profile:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {principalBuyerOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="principalBuyerProfile"
                                    id="principalBuyerProfile"
                                    label="Profile of principal buyer:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {buyerProfileOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="fundsAvailability"
                                    id="fundsAvailability"
                                    label="Are the funds ready?"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {fundsOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="sourceOfFunds"
                                    id="sourceOfFunds"
                                    label="Source of funds:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {sourceOfFundsOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="proofOfFunds"
                                    id="proofOfFunds"
                                    label="Proof of funds available?"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {proofOfFundsOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="completionTimeline"
                                    id="completionTimeline"
                                    label="Buyer timeline to closing:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select an option</option>
                                    {completionOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="dealStructure"
                                    id="dealStructure"
                                    label="Preferred deal structure: (optional)"
                                >
                                    <option value="">Select an option</option>
                                    {dealStructureOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.formField}>
                                <FieldSelect
                                    name="countryLocation"
                                    id="countryLocation"
                                    label="Your location:"
                                    validate={required('Required')}
                                >
                                    <option disabled value="">Select a country</option>
                                    {countryOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </FieldSelect>
                            </div>

                            <div className={css.curencyFields}>
                                <FieldSelect
                                    id="currency"
                                    name="currency"
                                    label="Currency"
                                    defaultValue="GBP"
                                    validate={required("Required")}
                                >
                                    <option disabled value="">Currency...</option>
                                    {supportedParnexusCurrencies.map(c => {
                                        return <option key={c} value={c}>{c}</option>
                                    })}
                                </FieldSelect>

                                <FieldCurrencyInput
                                    className={css.budgetField}
                                    name="approximateBudget"
                                    id="approximateBudget"
                                    label="Approximate budget:"
                                    placeholder="Enter your approximate budget"
                                    currencyConfig={currencyConfig}
                                    validate={required('Required')}
                                    skipCurrencyDisplay={true}
                                />
                            </div>

                            <div className={css.formField}>
                                <FieldTextInput
                                    type="text"
                                    name="notes"
                                    id="notes"
                                    label="Additional notes: (optional)"
                                    placeholder="e.g. specific requirements, proof of funds available, preferred deal structure"
                                    validate={composeValidators(
                                        validateMessage,
                                        validateInputField(currentUser, undefined, listingId)
                                    )}
                                />
                            </div>

                            <div className={css.warningMessage}>
                                <FormattedMessage id="BuyerFormModal.warningMessage" />
                            </div>

                            {initiateInquiryError ? <p>
                                Failed to submit the form!
                            </p> : null}

                            <div className={css.buttonContainer}>
                                <Button
                                    type="submit"
                                    disabled={pristine || invalid || initiateInquiryInProgress}
                                    inProgress={initiateInquiryInProgress}
                                    className={css.submitButton}
                                >
                                    <FormattedMessage id="BuyerFormModal.submitFormButton" />
                                </Button>
                            </div>
                        </Form>
                    )}
                />
            </div>
        </Modal>
    );
};

export default BuyerFormModal;
